import * as React from "react";
import { cn } from "../../lib/utils";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";

export interface FormHeaderProps {
  /**
   * The label text to display
   */
  label: string;
  /**
   * Size variant that affects text size, icon size, and spacing
   */
  size?: "small" | "medium" | "large";
  /**
   * Whether to show "(optional)" text after the label
   */
  isOptional?: boolean;
  /**
   * Whether to show "*" (required indicator) after the label
   */
  isRequired?: boolean;
  /**
   * Tooltip heading text
   */
  infoHeading?: string;
  /**
   * Tooltip description text - if provided, shows the info icon
   */
  infoDescription?: string;
  /**
   * Link text to display on the right side
   */
  linkText?: string;
  /**
   * Link href or onClick handler
   */
  linkHref?: string;
  /**
   * Link click handler
   */
  onLinkClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  /**
   * HTML for attribute to associate with form input
   */
  htmlFor?: string;
  /**
   * Custom class name for the container
   */
  className?: string;
  /**
   * Custom class name for the label
   */
  labelClassName?: string;
  /**
   * Custom class name for the link
   */
  linkClassName?: string;
}

const FormHeader = React.forwardRef<HTMLDivElement, FormHeaderProps>(
  (
    {
      label,
      size = "medium",
      isOptional = false,
      isRequired = false,
      infoHeading,
      infoDescription,
      linkText,
      linkHref,
      onLinkClick,
      htmlFor,
      className,
      labelClassName,
      linkClassName,
    },
    ref
  ) => {
    // Size-based configurations
    const sizeConfig = {
      small: {
        textSize: "xsmall" as const,
        iconSize: 12,
        gap: "gap-1.5",
      },
      medium: {
        textSize: "small" as const,
        iconSize: 14,
        gap: "gap-2",
      },
      large: {
        textSize: "medium" as const,
        iconSize: 16,
        gap: "gap-2.5",
      },
    };

    const config = sizeConfig[size];

    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center justify-between px-1",
          config.gap,
          className
        )}
      >
        <div className={cn("flex items-center", config.gap)}>
          <label
            htmlFor={htmlFor}
            className={cn("flex items-center", labelClassName)}
          >
            <Text
              as="span"
              variant="body"
              size={config.textSize}
              weight="semibold"
              color="subtle"
            >
              {label}
            </Text>
            {isRequired && (
              <Text
                as="span"
                variant="body"
                size={config.textSize}
                weight="semibold"
                color="negative"
                className="ml-0.5"
              >
                *
              </Text>
            )}
            {isOptional && (
              <Text
                as="span"
                variant="body"
                size={config.textSize}
                weight="regular"
                className="text-surface-ink-neutral-muted italic ml-1"
              >
                (optional)
              </Text>
            )}
          </label>
          {infoDescription && (
            <Tooltip description={infoDescription} heading={infoHeading}>
              <svg
                width={config.iconSize}
                height={config.iconSize}
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-surface-ink-neutral-muted"
              >
                <circle
                  cx="7"
                  cy="7"
                  r="6"
                  stroke="currentColor"
                  strokeWidth="1"
                />
                <path
                  d="M7 6V10M7 4.5V4"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeLinecap="round"
                />
              </svg>
            </Tooltip>
          )}
        </div>

        {linkText && (
          <a
            href={linkHref}
            onClick={onLinkClick}
            className={cn(
              "text-surface-ink-primary-normal hover:text-surface-ink-primary-hover transition-colors cursor-pointer font-display font-semibold leading-tight shrink-0",
              size === "small" && "text-xs",
              size === "medium" && "text-xs",
              size === "large" && "text-sm",
              linkClassName
            )}
          >
            {linkText}
          </a>
        )}
      </div>
    );
  }
);

FormHeader.displayName = "FormHeader";

export { FormHeader };
